import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetsmsapisentdetailQuery } from "../store/services/smsApi";
import { useGetapicampaigndetailsQuery } from "../store/services/rcstemplateApi";
import { useLazyWhatsdetailappReportQuery } from "../store/services/whatsappApi";
import { layoutCSS } from "../component/common/commonStyle";

// ─── SMS status filter configuration ────────────────────────────────────────
const SMS_STATUS_CONFIG = [
  { key: "Total", apiKey: null },
  { key: "AWAITED", apiKey: "AWAITED" },
  { key: "DELIVRD", apiKey: "DELIVRD" },
  { key: "FAILED", apiKey: "FAILED" },
  { key: "REJECTED", apiKey: "REJECTED" },
];

// ─── RCS status filter configuration (keys map to `overview`) ───────────────
const RCS_STATUS_CONFIG = [
  { key: "Total", apiKey: "total" },
  { key: "Sent", apiKey: "sent" },
  { key: "Delivered", apiKey: "delivered" },
  { key: "Read", apiKey: "read" },
  { key: "Pending", apiKey: "pending" },
  { key: "Failed", apiKey: "failed" },
];

// ─── WhatsApp status filter configuration ───────────────────────────────────
const WHATSAPP_STATUS_CONFIG = [
  { key: "Total", apiKey: null },
  { key: "Sent", apiKey: "sent" },
  { key: "Delivered", apiKey: "delivered" },
  { key: "Read", apiKey: "read" },
  { key: "Failed", apiKey: "failed" },
  { key: "Replied", apiKey: "replied" },
];

const getStatusColor = (status) => {
  switch ((status || "").toUpperCase()) {
    case "DELIVRD":
    case "DELIVERED":
    case "EXECUTED":
      return "success";
    case "AWAITED":
    case "PENDING":
      return "warning";
    case "FAILED":
    case "REJECTED":
    case "UNDELIVERED":
      return "error";
    case "SENT":
    case "READ":
      return "info";
    default:
      return "default";
  }
};

export default function Apisentreport() {
  const navigate = useNavigate();

  const { state } = useLocation();
  const gatewayRequestId = state?.gatewayRequestId;   // SMS
  const rcsRequestId = state?.rcsRequestId;           // RCS
  const whatsappCustomerId = state?.customerId;       // WhatsApp
  const whatsappCampaignId = state?.campaignId;       // WhatsApp
  const whatsappStartDate = state?.startDate;         // optional
  const whatsappEndDate = state?.endDate;             // optional

  const isRcs = !!rcsRequestId;
  const isWhatsapp = !!whatsappCampaignId;
  const isSms = !!gatewayRequestId;

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState("Total");

  // ── SMS query — no server-side status filter ────────────────────────────
  const smsResult = useGetsmsapisentdetailQuery(
    {
      request_id: gatewayRequestId,
      page,
      limit: rowsPerPage,
    },
    { skip: !gatewayRequestId }
  );

  // ── RCS query — no server-side status filter ────────────────────────────
  const rcsResult = useGetapicampaigndetailsQuery(
    {
      apirequest_id: rcsRequestId,
      page,
      limit: rowsPerPage,
    },
    { skip: !rcsRequestId }
  );

  // ── WhatsApp lazy query ─────────────────────────────────────────────────
  const [
    triggerWhatsapp,
    {
      data: whatsappResult,
      isLoading: whatsappLoading,
      isFetching: whatsappFetching,
      error: whatsappError,
    },
  ] = useLazyWhatsdetailappReportQuery();

  useEffect(() => {
    if (isWhatsapp && whatsappCampaignId) {
      triggerWhatsapp({
        customerId: whatsappCustomerId,
        campaign_id: whatsappCampaignId,
        page,
        limit: rowsPerPage,
        ...(whatsappStartDate && { start_date: whatsappStartDate }),
        ...(whatsappEndDate && { end_date: whatsappEndDate }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWhatsapp, whatsappCampaignId, whatsappCustomerId, page, rowsPerPage]);

  // ── Normalize the active result ─────────────────────────────────────────
  const activeResult = isWhatsapp
    ? {
        data: whatsappResult,
        isLoading: whatsappLoading,
        isFetching: whatsappFetching,
        isError: !!whatsappError,
        error: whatsappError,
      }
    : isRcs
    ? rcsResult
    : smsResult;

  const {
    data: report,
    isLoading,
    isFetching,
    isError,
    error,
  } = activeResult;

  // ─── Pull apart the response shape ──────────────────────────────────────
  const request = report?.request;
  const pagination = report?.pagination || {};

  const whatsappCampaigns = isWhatsapp ? report?.data || [] : [];
  const whatsappMessages = isWhatsapp
    ? whatsappCampaigns.flatMap((camp) =>
        (camp.messages || []).map((m) => ({ ...m, _campaign: camp }))
      )
    : [];

  const whatsappSummary = isWhatsapp ? report?.summary || {} : {};

  const rows = isWhatsapp ? whatsappMessages : report?.data || [];

  const overview = !isWhatsapp ? report?.overview || {} : {};
  const statSummary = !isWhatsapp ? report?.statSummary || {} : {};

  // ─── Chip counts (Delivery Statistics strip) ────────────────────────────
  const categories = useMemo(() => {
    if (isWhatsapp) {
      const byStatus = whatsappSummary?.by_status || {};
      const total = whatsappSummary?.total_messages ?? 0;
      return WHATSAPP_STATUS_CONFIG.map((c) => ({
        ...c,
        count: c.key === "Total" ? total : byStatus[c.apiKey] ?? 0,
      }));
    }
    if (isRcs) {
      return RCS_STATUS_CONFIG.map((c) => ({
        ...c,
        // RCS "Total" comes from `overview.total` — the count of unique
        // recipients — NOT from pagination.totalRecords (which counts data
        // rows and can be inflated by retries) and NOT from
        // request.total_recipients.
        count:
          c.key === "Total" ? overview.total ?? 0 : overview[c.apiKey] ?? 0,
      }));
    }
    const totalFromSummary = Object.values(statSummary).reduce(
      (sum, v) => sum + (Number(v) || 0),
      0
    );
    const total = totalFromSummary || pagination?.totalRecords || 0;
    return SMS_STATUS_CONFIG.map((c) => ({
      ...c,
      count: c.key === "Total" ? total : statSummary?.[c.apiKey] ?? 0,
    }));
  }, [isWhatsapp, isRcs, whatsappSummary, overview, statSummary, pagination]);

  // ─── Pagination field normalization ─────────────────────────────────────
  const currentPage = isWhatsapp
    ? pagination?.page ?? 1
    : isRcs
    ? pagination?.page ?? 1
    : pagination?.currentPage ?? 1;

  const totalPages = isWhatsapp
    ? pagination?.total_pages ?? 1
    : isRcs
    ? pagination?.totalPages ?? 1
    : pagination?.totalPages ?? 1;

  const totalRecords = isWhatsapp
    ? pagination?.total ?? 0
    : pagination?.totalRecords ?? 0;

  // Header "Total:" — for RCS use overview.total (unique recipients), not the
  // pagination count (which is inflated by retries).
  const headerTotal = isRcs ? overview?.total ?? 0 : totalRecords;

  // ─── Formatting helpers ─────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const d = dayjs(dateString);
    if (!d.isValid()) return "—";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
        <Typography
          variant="caption"
          sx={{ fontSize: "0.7rem", fontWeight: 500 }}
        >
          {d.format("DD/MM/YYYY")}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: "0.65rem", color: "text.secondary" }}
        >
          {d.format("HH:mm")}
        </Typography>
      </Box>
    );
  };

  const parseProviderResponse = (resp) => {
    if (!resp) return "—";
    try {
      const parsed = typeof resp === "string" ? JSON.parse(resp) : resp;
      return parsed?.message || JSON.stringify(parsed);
    } catch {
      return resp;
    }
  };

  // ─── Client-side row filter (SMS / RCS / WhatsApp) ─────────────────────
  const filteredRows = useMemo(() => {
    if (statusFilter === "Total") return rows;

    if (isWhatsapp) {
      const wanted = statusFilter.toLowerCase();
      return rows.filter((row) => {
        const current = (row.status || "").toLowerCase();

        if (wanted === "sent") return true;
        if (wanted === "delivered")
          return current === "delivered" || current === "read";
        if (wanted === "read") return current === "read";
        if (wanted === "failed") return current === "failed";
        if (wanted === "replied")
          return (
            (row.replied || 0) > 0 || (row.by_status?.replied || 0) > 0
          );
        return current === wanted;
      });
    }

    if (isRcs) {
      const wanted = statusFilter.toUpperCase();
      return rows.filter(
        (row) => (row.message_status || "").toUpperCase() === wanted
      );
    }

    // SMS
    const wanted = statusFilter.toUpperCase();
    return rows.filter(
      (row) => (row.status || "").toUpperCase() === wanted
    );
  }, [rows, statusFilter, isWhatsapp, isRcs]);

  // ─── Missing-state guard ────────────────────────────────────────────────
  if (!gatewayRequestId && !rcsRequestId && !whatsappCampaignId) {
    return (
      <Box sx={{ ...layoutCSS, py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No report selected. Go back and pick a row to view details.
        </Typography>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          size="small"
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ ...layoutCSS, py: 1 }}>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
          >
            {isWhatsapp
              ? "WhatsApp API Sent Report"
              : isRcs
              ? "RCS API Sent Report"
              : "SMS API Sent Report"}
          </Typography>
        </Box>
      </Box>

      {isLoading && !report ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={200}
        >
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Typography variant="body2" color="error" textAlign="center" py={4}>
          {error?.data?.message || "Failed to load report"}
        </Typography>
      ) : (
        <>
          {/* ── Overview ─────────────────────────────────────────────── */}
          <Grid container spacing={1} mb={2}>
            {/* Request Info */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 1, borderRadius: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  Request Info
                </Typography>
                <Grid container spacing={0.5}>
                  {isWhatsapp ? (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Campaign ID:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {whatsappCampaigns?.[0]?.campaign_id ||
                            whatsappCampaignId ||
                            "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Campaign Name:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {whatsappCampaigns?.[0]?.campaign_name || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Source:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {whatsappCampaigns?.[0]?.source || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Channel:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {whatsappCampaigns?.[0]?.send_channel || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Template:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {whatsappMessages?.[0]?.message_data?.template?.name ||
                            "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Recipients:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {whatsappCampaigns?.[0]?.total_no_of_phone_numbers ??
                            "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Sent At:
                        </Typography>
                        {formatDate(
                          whatsappCampaigns?.[0]?.status_timestamps?.sent_at
                        )}
                      </Grid>
                    </>
                  ) : isRcs ? (
                    <>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Campaign:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.campaign_name || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Bot:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.bot_name || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Template:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.template_name || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Template Type:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.template_type || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Provider:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.provider || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Status:
                        </Typography>
                        <Chip
                          label={request?.status || "—"}
                          size="small"
                          color={getStatusColor(request?.status)}
                          sx={{
                            fontSize: "0.65rem",
                            height: 18,
                            textTransform: "capitalize",
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Created:
                        </Typography>
                        {formatDate(request?.created_at)}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Completed:
                        </Typography>
                        {formatDate(request?.completed_at)}
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Template:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.template_name || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          API Receive ID:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {request?.apireceive_id || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Processed:
                        </Typography>
                        <Chip
                          label={request?.processed_status || "—"}
                          size="small"
                          color={
                            (request?.processed_status || "").toLowerCase() ===
                            "processed"
                              ? "success"
                              : "default"
                          }
                          sx={{
                            fontSize: "0.65rem",
                            height: 18,
                            textTransform: "capitalize",
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "block",
                            fontSize: "0.7rem",
                          }}
                        >
                          Created:
                        </Typography>
                        {formatDate(request?.created_at)}
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Delivery Statistics */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 1, borderRadius: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  Delivery Statistics
                </Typography>

                <Grid
                  container
                  sx={{
                    border: "0.5px solid",
                    borderColor: "divider",
                    borderRadius: 0.5,
                    overflow: "hidden",
                  }}
                >
                  {categories.map((cat, i) => (
                    <Grid
                      item
                      xs
                      key={cat.key}
                      sx={{
                        p: 0.75,
                        borderRight:
                          i < categories.length - 1 ? "0.5px solid" : "none",
                        borderColor: "divider",
                        textAlign: "center",
                        backgroundColor:
                          cat.key === "Total"
                            ? "rgba(184,204,228,0.35)"
                            : "rgba(255,255,255,0.32)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          fontSize: "0.65rem",
                        }}
                      >
                        {cat.key}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                      >
                        {cat.count}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {isWhatsapp ? (
                    <>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Total Messages
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                        >
                          {whatsappSummary?.total_messages ?? 0}
                        </Typography>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Success
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                          color="success.main"
                        >
                          {whatsappSummary?.successful_messages ?? 0}
                        </Typography>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Failed
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                          color="error.main"
                        >
                          {whatsappSummary?.failed_messages ?? 0}
                        </Typography>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Clicked
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                        >
                          {whatsappSummary?.by_status?.clicked ?? 0}
                        </Typography>
                      </Paper>
                    </>
                  ) : isSms ? (
                    <>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Total Messages
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                        >
                          {pagination?.totalRecords ?? 0}
                        </Typography>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Page
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                        >
                          {currentPage} / {totalPages}
                        </Typography>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 0.75, borderRadius: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Refund (this page)
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="0.8rem"
                        >
                          ₹{" "}
                          {rows
                            .reduce(
                              (sum, r) =>
                                sum + (Number(r.refund_balance) || 0),
                              0
                            )
                            .toFixed(2)}
                        </Typography>
                      </Paper>
                    </>
                  ) : null}
                </Box>

                {isRcs && request?.refund_info && (
                  <Box
                    sx={{
                      mt: 1,
                      border: "0.5px solid",
                      borderColor: "divider",
                      borderRadius: 0.5,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{ px: 1, py: 0.25, backgroundColor: "#B8CCE4" }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          color: "#1F3864",
                          fontSize: "0.7rem",
                        }}
                      >
                        Refund Summary
                      </Typography>
                    </Box>
                    <Grid
                      container
                      sx={{ backgroundColor: "rgba(255,255,255,0.32)" }}
                    >
                      {[
                        [
                          "Total Refund (₹)",
                          request.refund_info?.total_refund_balance,
                        ],
                      ].map(([label, value], i, arr) => (
                        <Grid
                          item
                          xs
                          key={label}
                          sx={{
                            p: 0.5,
                            borderRight:
                              i < arr.length - 1 ? "0.5px solid" : "none",
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem", display: "block" }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                          >
                            {value != null ? Number(value).toFixed(2) : 0}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* ── Message Details header + filter chips ──────────────── */}
          <Box mb={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  display: "inline",
                }}
              >
                Message Details
                {(isLoading || isFetching) && (
                  <CircularProgress size={14} sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                {/* For RCS show overview.total (unique recipients), not
                    pagination.totalRecords (which includes retries). */}
                Total: {headerTotal}
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              mt={0.5}
              flexWrap="wrap"
            >
              {categories.map((cat) => (
                <Chip
                  key={cat.key}
                  label={cat.key}
                  size="small"
                  variant={statusFilter === cat.key ? "filled" : "outlined"}
                  onClick={() => {
                    setStatusFilter(cat.key);
                    setPage(1);
                  }}
                  sx={{
                    fontSize: "0.6rem",
                    height: 20,
                    cursor: "pointer",
                    fontWeight: statusFilter === cat.key ? 700 : 400,
                    bgcolor:
                      statusFilter === cat.key ? "#7c3aed" : "transparent",
                    color: statusFilter === cat.key ? "white" : "#7c3aed",
                    borderColor: "#7c3aed",
                    "&:hover": {
                      bgcolor:
                        statusFilter === cat.key
                          ? "#6d28d9"
                          : "rgba(124,58,237,0.08)",
                    },
                  }}
                />
              ))}

              <FormControl size="small" sx={{ ml: "auto", minWidth: 130 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{ fontSize: "0.65rem", height: 24 }}
                >
                  {categories.map((cat) => (
                    <MenuItem
                      key={cat.key}
                      value={cat.key}
                      sx={{ fontSize: "0.65rem" }}
                    >
                      {cat.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {statusFilter !== "Total" && (
              <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                >
                  Showing {filteredRows.length} record(s) for “{statusFilter}”
                </Typography>
                <Button
                  size="small"
                  onClick={() => setStatusFilter("Total")}
                  sx={{
                    fontSize: "0.6rem",
                    p: 0,
                    minWidth: 0,
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Table ──────────────────────────────────────────────── */}
          <TableContainer component={Paper} sx={{ borderRadius: 0.5 }}>
            <Table
              size="small"
              sx={{ "& th, & td": { py: 0.5, px: 1, fontSize: "0.7rem" } }}
            >
              {isWhatsapp ? (
                <TableHead sx={{ background: "#B8CCE4" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Message ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Delivered</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Read</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                  </TableRow>
                </TableHead>
              ) : isRcs ? (
                <TableHead sx={{ background: "#B8CCE4" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Message ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Send Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Done Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Refund (₹)
                    </TableCell>
                  </TableRow>
                </TableHead>
              ) : (
                <TableHead sx={{ background: "#B8CCE4" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sender</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Count
                    </TableCell>
                   
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Done</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Provider Response
                    </TableCell>
                  </TableRow>
                </TableHead>
              )}

              <TableBody sx={{ background: "#ffffff6b" }}>
                {isLoading || isFetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={isWhatsapp ? 10 : isRcs ? 9 : 11}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length > 0 ? (
                  filteredRows.map((row, idx) =>
                    isWhatsapp ? (
                      <TableRow key={`${row.message_id}-${idx}`} hover>
                        <TableCell>
                          {(currentPage - 1) * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>
                          {row.message_data?.to || row.recipient_id || "—"}
                        </TableCell>
                        <TableCell>
                          {row.message_data?.template?.name || "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Tooltip title={row.message_id || ""} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 180,
                              }}
                            >
                              {row.message_id || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || "—"}
                            size="small"
                            color={getStatusColor(row.status)}
                            sx={{
                              fontSize: "0.6rem",
                              height: 18,
                              textTransform: "capitalize",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {row.booked_category ||
                            row.sent_category ||
                            row.message_data?.pricing?.category ||
                            "—"}
                        </TableCell>
                        <TableCell>{formatDate(row.sent_at)}</TableCell>
                        <TableCell>{formatDate(row.delivered_at)}</TableCell>
                        <TableCell>{formatDate(row.read_at)}</TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Tooltip
                            title={
                              row.message_data?.error?.error?.message ||
                              row.description ||
                              row.error_category ||
                              ""
                            }
                            arrow
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.message_data?.error?.error?.code
                                ? `[${row.message_data.error.error.code}] `
                                : ""}
                              {row.message_data?.error?.error?.message ||
                                row.description ||
                                row.message_data?.error_description ||
                                row.error_category ||
                                "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ) : isRcs ? (
                      <TableRow key={`${row.apireport_id}-${idx}`} hover>
                        <TableCell>
                          {(currentPage - 1) * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>{row.user}</TableCell>
                        <TableCell>{row.provider}</TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Tooltip title={row.message_id || ""} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 180,
                              }}
                            >
                              {row.message_id || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.message_status}
                            size="small"
                            color={getStatusColor(row.message_status)}
                            sx={{ fontSize: "0.6rem", height: 18 }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip title={row.reason || ""} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                wordBreak: "break-word",
                              }}
                            >
                              {row.reason || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatDate(row.send_time)}</TableCell>
                        <TableCell>{formatDate(row.done_time)}</TableCell>
                        <TableCell align="right">
                          {Number(row.refund_balance || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow
                        key={`${row.gateway_request_id}-${idx}`}
                        hover
                      >
                        <TableCell>
                          {(currentPage - 1) * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>{row.phone_number}</TableCell>
                        <TableCell>{row.sender_id}</TableCell>
                        <TableCell sx={{ maxWidth: 120 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              wordBreak: "break-word",
                            }}
                          >
                            {row.template_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Tooltip title={row.message || ""} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.message || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">{row.sms_count}</TableCell>
                        
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color={getStatusColor(row.status)}
                            sx={{ fontSize: "0.6rem", height: 18 }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(row.submitted_at)}
                        </TableCell>
                        <TableCell>{formatDate(row.done_at)}</TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Tooltip title={row.provider_response || ""} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                wordBreak: "break-word",
                              }}
                            >
                              {parseProviderResponse(row.provider_response)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={isWhatsapp ? 10 : isRcs ? 9 : 11}
                      align="center"
                      sx={{ py: 2 }}
                    >
                      {statusFilter !== "Total"
                        ? `No messages with status "${statusFilter}" found`
                        : "No message details found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Pagination ─────────────────────────────────────────── */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
                sx={{ fontSize: "0.7rem", height: 28 }}
              >
                {[25, 50, 100].map((num) => (
                  <MenuItem key={num} value={num} sx={{ fontSize: "0.7rem" }}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, v) => setPage(v)}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}